import { useEffect } from 'react';

interface SEOOptions {
  title?: string;
  description?: string;
  /** @deprecated use ogImage instead */
  image?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: 'summary' | 'summary_large_image';
  canonical?: string;
  url?: string;
}

const DEFAULT_TITLE = 'MD STUDIO - 미니디스크 라벨 제작 도구';
const DEFAULT_DESC =
  'MD STUDIO로 6종의 미니디스크 인쇄부를 실시간으로 디자인하고 고품질 PNG로 내보내세요.';

const DYNAMIC_META_KEYS: Array<[string, string]> = [
  ['property', 'og:type'],
  ['name', 'twitter:card'],
];

export function useSEO({
  title,
  description,
  image,
  ogImage,
  ogType,
  twitterCard,
  canonical,
  url,
}: SEOOptions = {}) {
  useEffect(() => {
    const finalTitle = title ? `${title} | MD STUDIO` : DEFAULT_TITLE;
    const finalDesc = description || DEFAULT_DESC;
    const finalUrl = url || window.location.href;
    const finalImage = ogImage || image;

    const previousTitle = document.title;
    document.title = finalTitle;

    updateMeta('name', 'description', finalDesc);

    updateMeta('property', 'og:title', finalTitle);
    updateMeta('property', 'og:description', finalDesc);
    updateMeta('property', 'og:url', finalUrl);
    if (finalImage) updateMeta('property', 'og:image', finalImage);
    if (ogType) updateMeta('property', 'og:type', ogType);

    updateMeta('name', 'twitter:title', finalTitle);
    updateMeta('name', 'twitter:description', finalDesc);
    if (finalImage) updateMeta('name', 'twitter:image', finalImage);
    if (twitterCard) updateMeta('name', 'twitter:card', twitterCard);

    let canonicalLink: HTMLLinkElement | null = null;
    if (canonical) {
      canonicalLink = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.href = canonical;
    }

    return () => {
      document.title = previousTitle;
      // cleanup dynamic-only tags added by this hook
      for (const [attr, key] of DYNAMIC_META_KEYS) {
        const el = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
        if (el) el.remove();
      }
      if (canonical && canonicalLink) {
        canonicalLink.remove();
      }
    };
  }, [title, description, image, ogImage, ogType, twitterCard, canonical, url]);
}

function updateMeta(attr: 'name' | 'property', key: string, value: string) {
  let meta = document.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attr, key);
    document.head.appendChild(meta);
  }
  meta.content = value;
}
